//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckmarkFilled, WarningAltFilled } from '@carbon/icons-react';
import LoopDataService from '../../../utils/LoopDataService';
import TenantDataService from '../../../utils/TenantDataService';
import { useTeamspaceContext } from '../../../TeamspaceContext';
import { useConfig } from '../../../Config';
import { useUserAuth } from '../../../UserAuth';
import {
  LoopUserUpdateTaskDto,
  TeamspaceUserUpdateTaskDto,
} from '../../Sidebar/SidebarTypes';

type TaskStatus = 'waiting' | 'success' | 'fail';
interface MemberUpdateTask {
  id: string;
  status: TaskStatus;
  renderedMessage: string;
  isProductTask: boolean;
  productName?: string;
  apiTaskType?: string;
  progressPercent: number;
}
interface UpdateMembersProgressProps {
  mode: 'loop' | 'teamspace';
  loopName?: string;
  loopId?: string;
  teamspaceId: string;
  finalUserList: string[];
  emailsToInvite: string[];
  originalUsers: string[];
  showSnackbar?: (message: string, type?: string) => void;
  onComplete?: () => void;
}

const TASK_SEQUENCE = [
  'PLAN',
  'TEST',
  'VELOCITY',
  'CONTROL',
  'DEPLOY',
  'BUILD',
];

/**
 * Shows real-time progress for member update operations in a loop or teamspace.
 *
 * @param {object} props The component props.
 * @param {'loop' | 'teamspace'} props.mode The display mode (loop/teamspace).
 * @param {string} [props.loopName] The name of the loop for display.
 * @param {string} [props.loopId] The ID of the loop (if in loop mode).
 * @param {string} props.teamspaceId The ID of the teamspace context.
 * @param {string[]} props.finalUserList The final list of member usernames.
 * @param {string[]} props.emailsToInvite A list of new emails to invite.
 * @param {string[]} props.originalUsers The original list of members before updating.
 * @param {function} [props.showSnackbar] A callback to show notifications.
 * @returns {JSX.Element} The rendered progress view component.
 */
function UpdateMembersProgress({
  mode,
  loopName,
  loopId,
  teamspaceId: contextTeamspaceId,
  finalUserList,
  emailsToInvite,
  originalUsers,
  showSnackbar,
}: Readonly<UpdateMembersProgressProps>) {
  const { t } = useTranslation();
  const { teamspaceName, tenantId, _setTeamspaceUsers } = useTeamspaceContext();
  const { services, polling } = useConfig();
  const { user, feature, isAuthenticated } = useUserAuth();

  const [allSuccessDialog, setAllSuccessDialog] = useState(false);
  const [errMsgSuccPage, setErrMsgSuccPage] = useState('');
  const [progress, setProgress] = useState(0);
  const [taskSequence, setTaskSequence] = useState<MemberUpdateTask[]>([]);

  const loopDataService = useMemo(
    () =>
      new LoopDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );
  const tenantDataService = useMemo(
    () =>
      new TenantDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );

  const addedKnownUsers = finalUserList.filter(
    (user) => !originalUsers.includes(user),
  );
  const addedUsers = [...addedKnownUsers, ...emailsToInvite];
  const removedUsers = originalUsers.filter(
    (user) => !finalUserList.includes(user),
  );

  const updateTaskState = useCallback(
    (
      index: number,
      status: TaskStatus,
      message?: string,
      progressPercent?: number,
    ) => {
      setTaskSequence((prevTasks) => {
        const newTasks = [...prevTasks];
        if (newTasks[index]) {
          const pct =
            progressPercent ??
            (status === 'success'
              ? 100
              : status === 'fail'
                ? 100
                : newTasks[index].progressPercent);
          newTasks[index] = {
            ...newTasks[index],
            status,
            renderedMessage: message || newTasks[index].renderedMessage,
            progressPercent: pct,
          };
        }
        return newTasks;
      });
    },
    [],
  );

  useEffect(() => {
    const runUpdateSequence = async () => {
      const initialTasks: MemberUpdateTask[] = [];
      let taskDtos: LoopUserUpdateTaskDto[] | TeamspaceUserUpdateTaskDto[] = [];
      const entityDisplayName =
        mode === 'loop' ? loopName : teamspaceName || contextTeamspaceId;

      initialTasks.push({
        id: 'initial_setup',
        renderedMessage: t('initial-setup-message', {
          entity: entityDisplayName || t('entity'),
        }),
        status: 'waiting',
        isProductTask: false,
        progressPercent: 0,
      });

      // fetching which products are available in the loop/tenant
      try {
        const response = await loopDataService.loopTaskStatus(
          contextTeamspaceId,
          feature ?? '',
          '',
        );
        const productsCategory = response.categories.find(
          (c) => c.displayName === 'Products',
        );
        const valueStreamCategory = response.categories.find(
          (c) => c.displayName === 'Valuestream',
        );

        const dynamicTasks: { key: string; name: string; visible: boolean }[] =
          [];
        if (productsCategory?.tasks)
          dynamicTasks.push(
            ...productsCategory.tasks.map((t) => ({
              key: t.type,
              name: t.displayName,
              visible: t.visible,
            })),
          );
        if (valueStreamCategory?.tasks)
          dynamicTasks.push(
            ...valueStreamCategory.tasks.map((t) => ({
              key: t.type,
              name: t.displayName,
              visible: t.visible,
            })),
          );

        const productTasks = dynamicTasks.filter(
          (task) => task.visible && TASK_SEQUENCE.includes(task.key),
        );

        productTasks.forEach((product) => {
          initialTasks.push({
            id: product.key,
            productName: product.name,
            apiTaskType:
              mode === 'loop'
                ? `${product.key}_UPDATE_LOOP`
                : `${product.key}_UPDATE_TEAMSPACE`,
            status: 'waiting',
            renderedMessage: t('preparing-product-update', {
              productName: product.name,
            }),
            isProductTask: true,
            progressPercent: 0,
          });
        });
      } catch (error) {
        console.error('Failed to fetch task sequence:', error);
        setErrMsgSuccPage(t('error-fetching-task-sequence'));
      }
      setTaskSequence(initialTasks);

      //call to teamspace/loop async API to add/remove members + product updates
      let mainAPICallSuccessful = false;
      try {
        setProgress(5);
        if (mode === 'teamspace' && emailsToInvite.length > 0) {
          await tenantDataService.inviteUsers(
            contextTeamspaceId,
            emailsToInvite,
          );
        }
        const consolidatedUserList = [...finalUserList, ...emailsToInvite];
        if (mode === 'loop') {
          // Handle member removal: update loop with final user list first
          if (removedUsers.length > 0) {
            await loopDataService.updateLoopUsers(
              contextTeamspaceId,
              loopId!,
              consolidatedUserList,
            );
          }

          // Always call addUsers to trigger product update tasks
          // Pass only the users being added (empty array if none)
          taskDtos = await loopDataService.addUsersToLoop(
            loopId!,
            addedUsers,
            true,
          );
        } else {
          // Teamspace mode: Handle member removal first, then add users with async API
          if (removedUsers.length > 0) {
            await tenantDataService.updateTeamspaceUsers(
              contextTeamspaceId,
              consolidatedUserList,
            );
          }

          // Always call addUsers to trigger product update tasks
          // Pass only the users being added (empty array if none)
          taskDtos = await tenantDataService.addUsersToTeamspace(
            contextTeamspaceId,
            addedUsers,
            true,
          );
        }
        mainAPICallSuccessful = true;
        setProgress(10);

        let initialSuccessMessage = t('initial-member-update-successful', {
          entity: entityDisplayName,
        });
        if (addedUsers.length > 0 && removedUsers.length === 0)
          initialSuccessMessage = t('initial-members-added', {
            count: addedUsers.length,
            entity: entityDisplayName,
          });
        else if (removedUsers.length > 0 && addedUsers.length === 0)
          initialSuccessMessage = t('initial-members-removed', {
            count: removedUsers.length,
            entity: entityDisplayName,
          });
        else if (addedUsers.length > 0 && removedUsers.length > 0)
          initialSuccessMessage = t('initial-members-updated-counts', {
            addCount: addedUsers.length,
            removeCount: removedUsers.length,
            entity: entityDisplayName,
          });

        updateTaskState(0, 'success', initialSuccessMessage);
      } catch (apiError: any) {
        mainAPICallSuccessful = false;
        let apiErrorMsg = apiError?.response?.data?.description;
        if (!apiErrorMsg) {
          if (addedUsers.length > 0 && removedUsers.length === 0) {
            apiErrorMsg =
              mode === 'loop'
                ? t('error-adding-loop-member')
                : t('error-adding-teamspace-member');
          } else if (removedUsers.length > 0 && addedUsers.length === 0) {
            apiErrorMsg =
              mode === 'loop'
                ? t('error-removing-loop-member')
                : t('error-removing-teamspace-member');
          } else {
            apiErrorMsg =
              mode === 'loop'
                ? t('error-modify-loop-member')
                : t('error-modify-teamspace-member');
          }
        }
        if (showSnackbar) showSnackbar(apiErrorMsg, 'error');
        setErrMsgSuccPage(apiErrorMsg);

        initialTasks.forEach((task, index) => {
          if (index === 0) {
            updateTaskState(index, 'fail', apiErrorMsg);
          } else {
            updateTaskState(
              index,
              'fail',
              t('task-skipped-due-to-previous-error', {
                productName: task.productName,
              }),
            );
          }
        });
      }

      // Poll for product task completion — backend handles task creation and execution
      if (mainAPICallSuccessful) {
        const productTasks = initialTasks.filter((task) => task.isProductTask);
        let allSubTasksSucceeded = true;

        const pollTaskStatusById = async (taskId: string): Promise<string> => {
          let attempts = 0;
          const maxRetries = polling?.maxRetries ?? 600;
          const interval = 2000; // Poll every 2 seconds as per requirement
          while (attempts < maxRetries) {
            attempts++;
            try {
              const resp =
                mode === 'loop'
                  ? await loopDataService.getTaskStatus(taskId)
                  : await tenantDataService.getTeamspaceTaskStatus(taskId);
              if (resp.status === 'COMPLETED') return 'COMPLETED';
              if (resp.status === 'FAILED') return 'FAILED';
            } catch (pollError) {
              if (attempts >= maxRetries)
                throw new Error(t('polling-failed-max-retries', { taskId }));
            }
            await new Promise((res) => setTimeout(res, interval));
          }
          return 'FAILED';
        };

        for (const task of productTasks) {
          const taskIndex = initialTasks.findIndex((t) => t.id === task.id);
          updateTaskState(
            taskIndex,
            'waiting',
            t('processing-product-update', { productName: task.productName }),
            30,
          );

          try {
            if (!task.apiTaskType)
              throw new Error(`apiTaskType is missing for task: ${task.id}`);

            // Find the corresponding task from the async API response
            const matchingTask = taskDtos.find(
              (dto) => dto.type === task.apiTaskType,
            );
            const taskId = matchingTask?.taskId;
            if (!taskId) {
              // Backend didn't create a task for this product (no sync needed)
              updateTaskState(
                taskIndex,
                'success',
                t('product-members-synced', {
                  productName: task.productName,
                }),
                100,
              );
              continue;
            }

            // Poll the task status
            const finalStatus = await pollTaskStatusById(taskId);

            if (finalStatus === 'COMPLETED') {
              let successMessage = t('product-members-synced', {
                productName: task.productName,
              });
              if (addedUsers.length > 0 && removedUsers.length === 0)
                successMessage = t('product-members-added', {
                  count: addedUsers.length,
                  productName: task.productName,
                });
              else if (removedUsers.length > 0 && addedUsers.length === 0)
                successMessage = t('product-members-removed', {
                  count: removedUsers.length,
                  productName: task.productName,
                });
              else if (addedUsers.length > 0 && removedUsers.length > 0)
                successMessage = t('product-members-updated-counts', {
                  addCount: addedUsers.length,
                  removeCount: removedUsers.length,
                  productName: task.productName,
                });
              updateTaskState(taskIndex, 'success', successMessage, 100);
            } else {
              throw new Error('Polling returned FAILED status');
            }
          } catch (error) {
            allSubTasksSucceeded = false;
            updateTaskState(
              taskIndex,
              'fail',
              t('error-adding-to-product', { productName: task.productName }),
              100,
            );
          }
        }
        setAllSuccessDialog(productTasks.length === 0 || allSubTasksSucceeded);

        // Refresh teamspace users to trigger GroupsPage automatic refresh
        if (productTasks.length === 0 || allSubTasksSucceeded) {
          try {
            const users =
              await tenantDataService.fetchTeamspaceMembers(contextTeamspaceId);
            const teamSpaceUsers = users.map((user: any) => ({
              id: user.id || user.keycloakId || '',
              username: user.username,
              fullname: user.fullname || '',
              keycloakId: user.keycloakId,
              firstname: user.firstname,
              lastname: user.lastname,
            }));
            _setTeamspaceUsers(teamSpaceUsers);
          } catch (error) {
            console.error('Error refreshing teamspace users:', error);
          }
        }
      }
      setProgress(100);
    };

    runUpdateSequence();
    // `runUpdateSequence` is intentionally run exactly once when this progress
    // view is mounted. The parent (`EditModal`) only mounts this component
    // after the user has confirmed the member update, and the prop snapshot
    // captured at mount defines the operation. Re-running the effect when
    // `finalUserList`, `emailsToInvite`, `originalUsers`, `showSnackbar`,
    // or the memoized services change would fire duplicate add/remove API
    // calls, so the exhaustive-deps rule is intentionally suppressed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTaskDisplayName = (task: MemberUpdateTask): string => {
    if (task.id === 'initial_setup') {
      return mode === 'loop'
        ? loopName || t('entity')
        : teamspaceName || t('entity');
    }
    return task.productName || task.id;
  };

  const getBarColorClass = (status: TaskStatus): string => {
    if (status === 'fail') return 'bg-destructive';
    return 'bg-status-successGreen';
  };

  const isSuccessPage = progress === 100 && allSuccessDialog && !errMsgSuccPage;

  return (
    <div
      id="id-update-members-container"
      data-testid="update-members-container"
      className="m-0 max-h-[544px] overflow-y-auto font-sans"
    >
      {errMsgSuccPage && (
        <div
          id="id-update-members-error-message"
          data-testid="update-members-error-message"
          className="mb-5 rounded border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm italic text-destructive dark:bg-destructive/20 dark:text-destructive"
        >
          {errMsgSuccPage}
        </div>
      )}

      {isSuccessPage ? (
        <div
          id="id-update-members-success-page"
          data-testid="update-members-success-page"
          className="flex min-h-[300px] flex-col items-center justify-center px-8 py-10 text-center"
        >
          {/* Green circle checkmark */}
          <div
            id="id-update-members-success-icon"
            data-testid="update-members-success-icon"
            className="mb-6 flex size-[72px] items-center justify-center rounded-full bg-status-successGreen"
          >
            <svg
              className="size-9 text-white"
              fill="none"
              stroke="currentColor"
              strokeWidth={3}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="mb-3 text-2xl font-semibold text-foreground">
            {mode === 'loop'
              ? t('loop-updated-successfully', 'Members Updated Successfully!')
              : t(
                  'teamspace-updated-successfully',
                  'Members Updated Successfully!',
                )}
          </h2>
          <p className="m-0 max-w-[360px] text-[15px] leading-relaxed text-muted-foreground">
            {mode === 'loop' ? t('your-loop') : t('your-teamspace')}{' '}
            <strong className="text-foreground">
              {loopName || teamspaceName}
            </strong>{' '}
            {t(
              addedUsers.length > 0 && removedUsers.length === 0
                ? 'member-added-successfully'
                : removedUsers.length > 0 && addedUsers.length === 0
                  ? 'member-removed-successfully'
                  : 'members_updated_successfully_suffix',
            )}
          </p>
        </div>
      ) : (
        <div
          id="id-update-members-task-list"
          data-testid="update-members-task-list"
          className="space-y-6"
        >
          {taskSequence.map((task, idx) => (
            <div
              key={`${task.id}-${idx}`}
              id={`id-update-members-task-${task.id}`}
              data-testid={`update-members-task-${task.id}`}
              className="flex items-start gap-4"
            >
              {/* Task name — left column */}
              <span
                id="id-update-members-task-name"
                data-testid="update-members-task-name"
                className="w-[140px] shrink-0 pt-0.5 text-sm font-bold text-foreground"
              >
                {getTaskDisplayName(task)}
              </span>
              {/* Status message + progress bar — right column */}
              <div
                id="id-update-members-task-status-wrapper"
                data-testid="update-members-task-status-wrapper"
                className="min-w-0 flex-1"
              >
                <div
                  id="id-update-members-task-status-row"
                  data-testid="update-members-task-status-row"
                  className="mb-1 flex items-center gap-2"
                >
                  {task.status === 'success' && (
                    <CheckmarkFilled
                      size={20}
                      className="shrink-0 text-status-successGreen"
                    />
                  )}
                  {task.status === 'fail' && (
                    <WarningAltFilled
                      size={20}
                      className="shrink-0 text-destructive"
                    />
                  )}
                  <span
                    id="id-update-members-task-status-message"
                    data-testid="update-members-task-status-message"
                    className={`truncate text-[13px] ${
                      task.status === 'fail'
                        ? 'text-destructive'
                        : task.status === 'success'
                          ? 'text-secondary-foreground'
                          : 'text-muted-foreground'
                    }`}
                  >
                    {task.status === 'waiting' && task.progressPercent === 0
                      ? t('status-waiting', 'Status: Waiting in Queue')
                      : task.renderedMessage}
                  </span>
                </div>
                {/* Progress bar */}
                <div
                  id="id-update-members-progress-bar-bg"
                  data-testid="update-members-progress-bar-bg"
                  className="h-2 w-full overflow-hidden rounded-full bg-muted"
                >
                  <div
                    id="id-update-members-progress-bar-fill"
                    data-testid="update-members-progress-bar-fill"
                    className={`h-full rounded-full transition-all duration-500 ease-out ${getBarColorClass(task.status)}`}
                    style={{
                      width: `${task.progressPercent}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default UpdateMembersProgress;
